import { useEffect, useMemo, useState } from "react";
import { Loader2, CheckCircle2, Info, Minus, Plus, Search, Wand2 } from "lucide-react";

import { Button } from "./ui/button";
import JobCardDetailModal from "./JobCardDetailModal";
import AddPartidaModal from "./AddPartidaModal";
import ModifyWithAIModal from "./ModifyWithAIModal";
import { capituloDeCodigo } from "../lib/capitulos";
import { fmtEUR } from "../lib/storage";

const AREA_LABELS = {
  falso_techo_pladur: "Falso techo de pladur",
  falso_techo: "Falso techo",
  alicatado_bano: "Alicatado del baño",
  alicatado: "Alicatado",
  pavimento_bano: "Suelo del baño",
  pavimento: "Suelo",
  demolicion_bano: "Demolición del baño",
  demolicion: "Demolición",
  iluminacion: "Iluminación",
  ventilacion_cocina: "Ventilación de cocina",
  ventilacion: "Ventilación",
  fontaneria_cocina: "Fontanería de cocina",
  fontaneria: "Fontanería",
  instalacion_electrica: "Instalación eléctrica",
  mobiliario_cocina: "Mobiliario de cocina",
  mobiliario_bano: "Mobiliario del baño",
  sanitarios_bano: "Sanitarios del baño",
  sanitarios: "Sanitarios",
  plato_ducha: "Plato de ducha",
  mampara_bano: "Mampara",
  mampara: "Mampara",
  pintura: "Pintura",
};

function humanizarArea(area) {
  if (!area) return "";
  if (AREA_LABELS[area]) return AREA_LABELS[area];
  return area
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

function fmtPrecio(n) {
  if (n == null) return "";
  return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(n);
}

const CANTIDAD_DEFECTO_POR_UNIDAD = {
  ud: 1,
  uds: 1,
  m: 5,
  ml: 5,
  "m²": 10,
  m2: 10,
  "m³": 1,
  m3: 1,
  kg: 10,
  l: 5,
  h: 1,
};

function cantidadDefecto(pregunta) {
  if (pregunta?.cantidad != null) return pregunta.cantidad;
  if (pregunta?.cantidad_sugerida != null) return pregunta.cantidad_sugerida;
  const u = (pregunta?.unidad ?? "").trim().toLowerCase();
  return CANTIDAD_DEFECTO_POR_UNIDAD[u] ?? 1;
}

function tituloOpcion(o) {
  const raw = o?.titulo ?? o?.texto ?? o?.jobcard_id ?? "";
  const primero = String(raw).split(".")[0];
  return primero || raw;
}

function Stepper({ value, unidad, onChange }) {
  const num = Number(value);
  const safe = Number.isFinite(num) ? num : 0;
  const dec = () => onChange(Math.max(0, safe - 1));
  const inc = () => onChange(safe + 1);
  return (
    <div className="inline-flex items-stretch rounded-md border border-slate-200 bg-white shrink-0 overflow-hidden">
      <button
        type="button"
        onClick={dec}
        className="px-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
        aria-label="Decrementar"
      >
        <Minus className="w-3.5 h-3.5" />
      </button>
      <input
        type="number"
        min="0"
        step="any"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-14 text-center tabular-nums font-bold text-sm text-slate-800 outline-none border-x border-slate-200 bg-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <span className="px-1.5 inline-flex items-center text-[10px] uppercase tracking-widest font-bold text-slate-500">
        {unidad ?? "ud"}
      </span>
      <button
        type="button"
        onClick={inc}
        className="px-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
        aria-label="Incrementar"
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function OpcionCard({ active, onClick, onInfo, titulo, precio, unidad }) {
  return (
    <div
      className={`relative rounded-lg border transition-all ${
        active
          ? "border-blue-600 bg-blue-50/50 ring-2 ring-blue-500/30"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left px-3 py-2.5 flex items-start gap-3"
      >
        <span
          className={`mt-0.5 shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
            active
              ? "border-blue-600 bg-blue-600"
              : "border-slate-300 bg-white"
          }`}
          aria-hidden="true"
        >
          {active ? (
            <span className="w-1.5 h-1.5 rounded-full bg-white" />
          ) : null}
        </span>
        <div className="flex-1 min-w-0">
          <div
            className={`text-sm leading-snug ${
              active ? "text-slate-900 font-semibold" : "text-slate-800"
            }`}
          >
            {titulo}
          </div>
          {precio != null ? (
            <div className="mt-0.5 text-[11px] tabular-nums font-bold text-blue-700">
              {fmtPrecio(precio)} €/{unidad ?? "ud"}
            </div>
          ) : null}
        </div>
      </button>
      {onInfo ? (
        <button
          type="button"
          onClick={onInfo}
          className="absolute top-1.5 right-1.5 p-1 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50"
          title="Ver detalle"
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      ) : null}
    </div>
  );
}

export default function MotorAnswers({
  preguntas,
  intenciones,
  onSubmit,
  loading,
  onAddPartida,
  onModifyWithAI,
}) {
  const initial = useMemo(() => {
    const map = {};
    for (const p of preguntas) {
      map[p.area] = {
        jobcard_id: p.opciones?.[0]?.jobcard_id ?? null,
        cantidad: cantidadDefecto(p),
      };
    }
    return map;
  }, [preguntas]);

  const [answers, setAnswers] = useState(initial);
  const [detalle, setDetalle] = useState(null);
  const [addModal, setAddModal] = useState({ open: false, query: "", contexto: null });
  const [aiModalOpen, setAiModalOpen] = useState(false);

  useEffect(() => {
    setAnswers(initial);
  }, [initial]);

  const jobcardIdByArea = useMemo(() => {
    const map = new Map();
    for (const i of intenciones ?? []) {
      const candidatas = i.candidatas ?? [];
      const elegida =
        candidatas.find((c) => c.id === i.jobcard_id) ?? candidatas[0];
      if (elegida?.id) map.set(i.area, elegida.id);
    }
    return map;
  }, [intenciones]);

  const gruposPreguntas = useMemo(() => {
    const grupos = [];
    const indice = new Map();
    preguntas.forEach((p, idx) => {
      const seleccion = answers[p.area]?.jobcard_id ?? null;
      const refId =
        seleccion ??
        p.opciones?.[0]?.jobcard_id ??
        jobcardIdByArea.get(p.area) ??
        null;
      const cap = capituloDeCodigo(refId);
      let g = indice.get(cap.id);
      if (!g) {
        g = { capitulo: cap, preguntas: [], firstIdx: idx };
        grupos.push(g);
        indice.set(cap.id, g);
      }
      g.preguntas.push(p);
    });
    grupos.sort((a, b) => {
      if (a.capitulo.orden !== b.capitulo.orden)
        return a.capitulo.orden - b.capitulo.orden;
      return a.firstIdx - b.firstIdx;
    });
    return grupos;
  }, [preguntas, answers, jobcardIdByArea]);

  const setEleccion = (area, jobcard_id) =>
    setAnswers((prev) => ({ ...prev, [area]: { ...prev[area], jobcard_id } }));

  const setCantidad = (area, cantidad) =>
    setAnswers((prev) => ({ ...prev, [area]: { ...prev[area], cantidad } }));

  const lineas = preguntas.map((p) => {
    const a = answers[p.area] ?? {};
    const opcion =
      (p.opciones ?? []).find((o) => o.jobcard_id === a.jobcard_id) ?? null;
    const cantidadNum = Number(a.cantidad);
    const cantidad = Number.isFinite(cantidadNum) ? cantidadNum : 0;
    const precio = Number(opcion?.precio_unitario) || 0;
    return { p, a, opcion, cantidad, precio, importe: cantidad * precio };
  });

  const totalLive = lineas.reduce((s, l) => s + l.importe, 0);
  const completas = lineas.filter(
    (l) => (l.opcion || (l.p.tipo === "cantidad")) && l.cantidad > 0,
  ).length;

  const allReady = preguntas.every((p) => {
    const a = answers[p.area];
    if (!a) return false;
    const necesitaSeleccion = p.tipo !== "cantidad";
    if (necesitaSeleccion && !a.jobcard_id) return false;
    const n = Number(a.cantidad);
    if (!Number.isFinite(n) || n <= 0) return false;
    return true;
  });

  const handleSubmit = () => {
    const choices = preguntas.map((p) => {
      const a = answers[p.area] ?? {};
      const n = Number(a.cantidad);
      return {
        area: p.area,
        jobcard_id: a.jobcard_id ?? null,
        cantidad: Number.isFinite(n) && n > 0 ? n : null,
      };
    });
    onSubmit(choices);
  };

  return (
    <div className="space-y-2">
      <div
        className="sticky top-0 z-20 -mx-2 px-2 py-2 bg-white/85 backdrop-blur border-b border-slate-200/70 flex items-center gap-3"
        data-testid="motor-answers-header"
      >
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 text-blue-700 font-display font-bold text-[11px]">
            {completas}/{preguntas.length}
          </span>
          <span className="hidden sm:inline">partidas listas</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {onModifyWithAI ? (
            <Button
              onClick={() => setAiModalOpen(true)}
              size="sm"
              variant="outline"
              className="gap-1.5 border-purple-300 text-purple-700 hover:bg-purple-50 hover:border-purple-400"
              data-testid="motor-modify-ai-btn"
            >
              <Wand2 className="w-3.5 h-3.5" />
              Modificar con IA
            </Button>
          ) : null}
          {onAddPartida ? (
            <Button
              onClick={() => setAddModal({ open: true, query: "", contexto: null })}
              size="sm"
              variant="outline"
              className="gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50 hover:border-blue-400"
              data-testid="motor-add-partida-step2-btn"
            >
              <Plus className="w-3.5 h-3.5" />
              Añadir partida
            </Button>
          ) : null}
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold leading-none">
              Total estimado
            </div>
            <div className="font-display font-bold text-base tabular-nums leading-tight text-slate-900">
              {fmtEUR(totalLive)}
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={!allReady || loading}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 gap-1.5"
            data-testid="motor-confirm-btn"
          >
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="w-3.5 h-3.5" />
            )}
            Confirmar
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {gruposPreguntas.map((grupo, gIdx) => (
          <section
            key={`cap-${grupo.capitulo.id}`}
            data-testid={`answer-capitulo-${grupo.capitulo.id}`}
          >
            <header className="flex items-center gap-2 mb-1.5 px-1">
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400 tabular-nums">
                {String(gIdx + 1).padStart(2, "0")}
              </span>
              <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500">
                {grupo.capitulo.nombre}
              </span>
              <div className="flex-1 h-px bg-slate-200/80" />
            </header>
            <ul className="space-y-1.5">
              {grupo.preguntas.map((p) => {
                const a = answers[p.area] ?? {};
                const linea = lineas.find((l) => l.p === p);
                const opciones = p.opciones ?? [];
                return (
                  <li
                    key={p.area}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-3 hover:border-slate-300 transition-colors"
                    data-testid={`answer-card-${p.area}`}
                  >
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold text-sm text-slate-800">
                          {humanizarArea(p.area)}
                        </div>
                        {p.tipo !== "cantidad" && opciones.length > 0 ? (
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            Elige una opción ({opciones.length} disponibles)
                          </div>
                        ) : null}
                      </div>
                      <Stepper
                        value={a.cantidad ?? ""}
                        unidad={p.unidad ?? "ud"}
                        onChange={(v) => setCantidad(p.area, v)}
                      />
                      <div className="text-right shrink-0 min-w-[80px]">
                        <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold leading-none">
                          Importe
                        </div>
                        <div className="font-display font-bold text-sm tabular-nums leading-tight text-slate-900">
                          {fmtEUR(linea?.importe ?? 0)}
                        </div>
                      </div>
                    </div>
                    {p.tipo !== "cantidad" && opciones.length > 0 ? (
                      <>
                        <div
                          className="grid grid-cols-1 md:grid-cols-2 gap-2"
                          data-testid={`opciones-${p.area}`}
                        >
                          {opciones.map((o) => (
                            <div
                              key={o.jobcard_id}
                              data-testid={`opcion-${p.area}-${o.jobcard_id}`}
                            >
                              <OpcionCard
                                active={a.jobcard_id === o.jobcard_id}
                                onClick={() => setEleccion(p.area, o.jobcard_id)}
                                onInfo={() => setDetalle(o)}
                                titulo={tituloOpcion(o)}
                                precio={o.precio_unitario}
                                unidad={o.unidad ?? p.unidad}
                              />
                            </div>
                          ))}
                        </div>
                        {onAddPartida ? (
                          <div className="mt-2 flex justify-end">
                            <button
                              type="button"
                              onClick={() =>
                                setAddModal({
                                  open: true,
                                  query: humanizarArea(p.area),
                                  contexto: humanizarArea(p.area),
                                })
                              }
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700"
                              data-testid={`opciones-add-more-${p.area}`}
                            >
                              <Search className="w-3.5 h-3.5" />
                              Buscar más opciones similares
                            </button>
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      <JobCardDetailModal
        open={!!detalle}
        onClose={() => setDetalle(null)}
        jobcard={detalle}
      />

      <AddPartidaModal
        open={addModal.open}
        initialQuery={addModal.query}
        contextoTitulo={addModal.contexto}
        onClose={() => setAddModal({ open: false, query: "", contexto: null })}
        onAdd={async ({ jobcard, cantidad }) => {
          if (onAddPartida) {
            await onAddPartida({ jobcard, cantidad });
          }
          setAddModal({ open: false, query: "", contexto: null });
        }}
        loading={loading}
      />

      <ModifyWithAIModal
        open={aiModalOpen}
        loading={loading}
        onClose={() => setAiModalOpen(false)}
        onSubmit={async (prompt) => {
          if (!onModifyWithAI) return;
          const ok = await onModifyWithAI(prompt);
          if (ok) setAiModalOpen(false);
        }}
      />
    </div>
  );
}
